// btw-front/src/api/adminDeals.js
import { api, getAccessToken } from "./client";

export async function list({ status, partnerId, quarter, q } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (partnerId) params.set("partnerId", partnerId);
  if (quarter) params.set("quarter", quarter);
  if (q) params.set("q", q);
  const qs = params.toString();
  const res = await api.get(`/admin/deals${qs ? `?${qs}` : ""}`);
  return res.items || [];
}

export async function get(id) {
  return api.get(`/admin/deals/${encodeURIComponent(id)}`);
}

export async function update(id, payload) {
  return api.put(`/admin/deals/${encodeURIComponent(id)}`, payload);
}

export async function create(payload) {
  return api.post(`/admin/deals`, payload);
}

// 소프트 삭제 — reason(선택, 500자 이내)은 관리자 [삭제된 딜] 열람 페이지에 표시
export async function remove(id, reason) {
  return api.del(`/admin/deals/${encodeURIComponent(id)}`, { body: { reason: reason || "" } });
}

// 삭제된 딜 목록 (열람 전용) — { items: [...] }
export async function listDeleted() {
  const res = await api.get(`/admin/deleted-deals`);
  return res.items || [];
}

export async function listChanges(id) {
  const res = await api.get(`/admin/deals/${encodeURIComponent(id)}/changes`);
  return res.items || [];
}

// 딜 관리자 메시지(파트너 공개 스레드) — { items: [...] }, 메일 상태 포함
export async function listMessages(id) {
  const res = await api.get(`/admin/deals/${encodeURIComponent(id)}/messages`);
  return res.items || [];
}

// 메시지 작성 + 영업대표 알림 메일 — 응답 item 은 메일 결과(mailStatus) 반영된 최종 행
export async function createMessage(id, content) {
  const res = await api.post(`/admin/deals/${encodeURIComponent(id)}/messages`, { content });
  return res.item;
}

// 견적서 업로드 (multipart). file.name 을 별도 form field 로 같이 전송 — multer
// originalname 인코딩 손상 시 서버가 이 값을 우선 사용해 한국어 파일명 보존.
export async function uploadQuotation(id, file) {
  const fd = new FormData();
  fd.append("quotation", file);
  fd.append("filename", file.name);
  return api.postForm(`/admin/deals/${encodeURIComponent(id)}/quotation`, fd);
}

// 견적서 다운로드 — Authorization 헤더 필요해 fetch 직접 사용 후 Blob 처리
export async function downloadQuotation(id, suggestedName) {
  const token = getAccessToken();
  const res = await fetch(`/api/admin/deals/${encodeURIComponent(id)}/quotation`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = "Download failed";
    try {
      const j = await res.json();
      msg = (j && j.error && j.error.message) || msg;
    } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName || "quotation";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// 도입(adoption) 전환 — multipart: files[](≥1, 기존 증빙 파일이 있으면 생략 가능) + partnerMemo(선택).
// 서버가 status 변경·처리자 기록·파일 저장을 한 트랜잭션으로 처리. 응답 { ok, adoptionFiles[] }.
export async function adopt(id, { files, partnerMemo } = {}) {
  const fd = new FormData();
  Array.from(files || []).forEach((f) => fd.append("files", f, f.name));
  if (partnerMemo) fd.append("partnerMemo", partnerMemo);
  return api.postForm(`/admin/deals/${encodeURIComponent(id)}/adoption`, fd);
}

// 증빙 파일 추가 — 도입 처리한 본인만(서버 403). 응답 { adoptionFiles[] }.
export async function uploadAdoptionFiles(id, files) {
  const fd = new FormData();
  Array.from(files || []).forEach((f) => fd.append("files", f, f.name));
  return api.postForm(`/admin/deals/${encodeURIComponent(id)}/adoption-files`, fd);
}

// 증빙 파일 삭제 — 도입 처리한 본인만. 도입 상태면 마지막 1개 삭제 불가(서버 400). 응답 { adoptionFiles[] }.
export async function removeAdoptionFile(id, fileId) {
  return api.del(
    `/admin/deals/${encodeURIComponent(id)}/adoption-files/${encodeURIComponent(fileId)}`,
  );
}

// 증빙 파일 다운로드 — Authorization 헤더 필요해 fetch 직접 사용 후 Blob 처리(견적서 패턴 동일)
export async function downloadAdoptionFile(id, fileId, suggestedName) {
  const token = getAccessToken();
  const res = await fetch(
    `/api/admin/deals/${encodeURIComponent(id)}/adoption-files/${encodeURIComponent(fileId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    let msg = "Download failed";
    try {
      const j = await res.json();
      msg = (j && j.error && j.error.message) || msg;
    } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName || "file";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// 담당자 변경(파트너사·영업대표 이관) — body { partnerId, partnerSalesRepId, partnerUserId? }.
// partnerUserId 는 소유 계정이 없는 옛 영업대표를 고를 때만 보낸다. 응답 { ok }.
// 등록자 기록은 서버가 그대로 두고, 권한은 새 담당 계정으로 넘어간다.
export async function changeOwner(id, payload) {
  return api.put(`/admin/deals/${encodeURIComponent(id)}/owner`, payload);
}
